import {useEffect, useRef} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import NextLink from 'next/link';
import {Container, Box, Heading, Image, Text, Link, Button} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {px, py} from '@eksab/theme';
import {useOnScreen} from '@eksab/hooks';
import {featuredInList} from '@eksab/features/home/featured-in/data';
import {getLanguage} from '@eksab/util/helpers';
import {GetStaticProps} from 'next';
import {dehydrate, QueryClient} from '@tanstack/react-query';
import {setApiLocale} from '@eksab/api';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';

export default function AboutUs() {
  const animatedSectionRef = useRef<HTMLImageElement>(null);
  const locale = useRouter().locale;

  const t = useTranslate();
  const isOnScreen = useOnScreen(animatedSectionRef, {threshold: 0.7});

  useEffect(() => {
    mixpanel?.track('Page View', {page: 'About Us'});
  }, []);

  return (
    <>
      <Head>
        <meta name="description" content="Predict game score and win prizes | Eksab" />
        <title>{`${t('app.about-us')} | ${t('app.name')}`}</title>
      </Head>

      <Container as="header" maxW="100vw" px="0" variant="pattern-dark" py={py}>
        <Container display="block">
          <Image
            src="/images/play-field.svg"
            alt=""
            w="clamp(10rem, 40vw, 25.625rem)"
            mt={['min(40vw, 40%)', '6em', null, '5em']}
            me={[0, null, -6]}
            float="inline-end"
            transform={`rotateY(${getLanguage(locale) === 'ar' ? 0.5 : 0}turn)`}
            sx={{
              shapeOutside: 'url(/images/play-field.svg)',
              '@supports not (float: inline-end)': {
                float: 'right',
              },
              '@supports not (transform: rotate)': {
                transform: 'auto',
              },
            }}
          />

          <Heading as="h1" size="4xl" lineHeight="1.5" color="green.400" maxW={{base: '15ch', xl: '20ch'}}>
            {t('about-us.header.heading')}
          </Heading>
        </Container>
      </Container>

      <Box as="section" bg="gray.600" maxW="100vw" py="4">
        <Container
          variant="scroll"
          h={[24, null, 36]}
          mx="auto"
          alignItems="center"
          justifyContent="space-between"
          gap="12"
        >
          {featuredInList[getLanguage(locale)].map(({name, title, logo, article}) => (
            <Link key={name} href={article} target="_blank">
              <Image
                src={logo}
                alt={name}
                title={title}
                py="1"
                minW={[28, null, 36]}
                maxH={[12, null, 16]}
                objectFit="contain"
                mixBlendMode="multiply"
              />
            </Link>
          ))}
        </Container>
      </Box>

      <Container as="section" py={[12, 16, 24]} flexDir="column" gap={[20, null, 16]} pos="relative">
        <Box
          w="100%"
          display="flex"
          flexDir={['column', null, 'row']}
          alignItems={['flex-start', 'center']}
          justifyContent="space-between"
          gap={[8, null, 20]}
        >
          <Box textAlign={['start', 'center', 'start']}>
            <Heading fontSize="clamp(1.25rem, 5vw, 2rem)" mb={[3, null, 8]}>
              {t('about-us.about.brief.heading')}
            </Heading>

            <Text fontSize={['md', null, 'xl']} lineHeight="2" maxW={['initial', null, '41ch']}>
              {t('about-us.about.brief.subheading')}
            </Text>
          </Box>

          <Image
            alignSelf="center"
            transition="transform 0.7s ease-in 0.3s, opacity 0.6s ease-in 0.3s"
            transform={[
              `rotateY(${getLanguage(locale) === 'ar' ? 0.5 : 0}turn)`,
              null,
              `rotateY(${getLanguage(locale) === 'ar' ? 0.5 : 0}turn) translateY(${isOnScreen ? '120%' : 0})`,
            ]}
            opacity={[1, null, isOnScreen ? 0 : 1]}
            w={[64, null, 72, 80, 96]}
            rounded="full"
            src="/images/football-fans.png"
            alt="Football fans excited"
          />
        </Box>

        <Box
          w="100%"
          display="flex"
          flexDir={['column', null, 'row']}
          alignItems="center"
          justifyContent="space-between"
          gap={[8, null, 20]}
        >
          <Box textAlign={['start', 'center', 'start']} ref={animatedSectionRef}>
            <Heading fontSize="clamp(1.25rem, 5vw, 2rem)" mb={[3, null, 8]}>
              {t('about-us.about.competitions.heading')}
            </Heading>

            <Text fontSize={['md', null, 'xl']} lineHeight="2" maxW={['initial', null, '41ch']}>
              {t('about-us.about.competitions.subheading', {
                strong: (chunks) => (
                  <Text as="strong" fontWeight="600" lang="ar">
                    {chunks}
                  </Text>
                ),
              })}
            </Text>
          </Box>

          <Box
            transition="transform 0.7s ease-in 0.3s, opacity 0.6s ease-in 0.3s"
            transform={['initial', null, `translateY(${isOnScreen ? 0 : '-115%'})`]}
            opacity={[1, null, isOnScreen ? 1 : 0]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            minW={[64, null, 72, 80, 96]}
            sx={{aspectRatio: '1/1'}}
            rounded="full"
            bg="gray.600"
            overflow="hidden"
          >
            <Image
              w={['100%', null]}
              src="/images/ministry-of-youth-and-sports-logo.png"
              alt="Ministry of Youth and Sports"
            />
          </Box>
        </Box>
      </Container>

      <Box as="section" bg="gray.600" maxW="100vw" py="16">
        <Container flexDir="column" alignItems="center" gap="8">
          <Heading size="sm" fontWeight="800" textAlign="center">
            {t('about-us.ceo-with-president')}
          </Heading>

          <Box rounded="md" overflow="hidden">
            <Image
              src="/images/ceo-with-president.jpg"
              alt="Eksab's CEO photographed with President Abdelfatah El Sisi"
              objectFit="contain"
              objectPosition="center"
              transition="transform 0.5s"
              transform={['scale(2.7)', 'scale(2.1)', 'scale(1.7)', 'scale(1.4)', 'scale(1)']}
              minH="96"
            />
          </Box>
        </Container>
      </Box>

      <Box as="footer" w="100%">
        <Container
          variant="pattern-dark"
          maxW="100%"
          px={px}
          py={[16, null, 24]}
          flexDir="column"
          alignItems="center"
          gap="3"
        >
          <Heading textAlign="center" fontSize={['1.3rem', null, '2rem']} color="white">
            {t('about-us.footer.heading')}
          </Heading>

          <Text textAlign="center" fontSize={['md', null, 'xl']} color="white">
            {t('about-us.footer.subheading')}
          </Text>

          <NextLink passHref href="https://yallaeksab.freshteam.com/jobs" legacyBehavior>
            <Button as="a" mt="8" variant="solid">
              {t('about-us.footer.cta')}
            </Button>
          </NextLink>
        </Container>
      </Box>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({locale}) => {
  const queryClient = new QueryClient();

  setApiLocale(locale);

  await queryClient.prefetchQuery([useRegionSettings.queryKey, locale], useRegionSettings.queryFn);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 60 * 60 * 3,
  };
};
